/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useMessageSeverity } from '../hooks/PRIVATE_useMessageSeverity';
import { useAccessibleContext } from '../hooks/UNSAFE_useAccessibleContext';
import { merge } from '../utils/UNSAFE_stringUtils';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';

type UseCheckboxRadioFieldProps = {
  ariaDescribedBy?: string;
  baseId: string;
  hasValue?: boolean;
  hasTopUserAssistance?: boolean;
  hasBottomUserAssistance?: boolean;
  isFocused?: boolean; // TODO: check if this is needed
  isReadonly?: boolean;
  isRequired?: boolean;
  label: string;
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  styleVariant?: 'default' | 'embedded'; // TODO: check if this is needed
  messages?: ComponentProps<typeof InlineUserAssistance>['messages'];
};

type GroupProps = {
  groupProps: {
    'aria-describedby'?: string;
    'aria-labelledby'?: string;
    'aria-required'?: boolean;
    'aria-invalid'?: boolean;
    id: string;
    readonly?: boolean;
  };
  labelProps: {
    forId: string;
    id: string;
    isRequiredShown?: boolean;
  };
  topUserAssistanceProps?: {
    id: string;
    variant: 'reflow';
  };
  bottomUserAssistanceProps?: {
    id: string;
    isRequiredShown: boolean;
  };
};

export function useCheckboxRadioField({
  ariaDescribedBy,
  baseId,
  hasValue,
  hasTopUserAssistance,
  hasBottomUserAssistance,
  isReadonly,
  isRequired = false,
  label,
  labelEdge,
  messages
}: UseCheckboxRadioFieldProps): GroupProps {
  const optionsGroupId = `${baseId}-group`;
  const labelId = `${baseId}-label`;
  const topUaId = hasTopUserAssistance ? `${baseId}-top-ua` : undefined;
  const bottomUaId = hasBottomUserAssistance ? `${baseId}-bottom-ua` : undefined;

  const { UNSAFE_ariaLabelledBy } = useAccessibleContext();
  const ariaLabelledBy = label === '' && labelEdge === 'none' ? UNSAFE_ariaLabelledBy : labelId;

  const highestMessageSeverity = useMessageSeverity(messages);
  const hasErrorMessage = highestMessageSeverity === 'error';

  return {
    groupProps: {
      'aria-describedby': merge([topUaId, bottomUaId, ariaDescribedBy]),
      'aria-labelledby': ariaLabelledBy,
      'aria-required': isRequired,
      ...(isReadonly && { readonly: true }),
      ...(hasErrorMessage && { 'aria-invalid': true }),
      id: optionsGroupId
      // TODO: ariaInvalid when one of message is error type?
    },
    labelProps: {
      forId: optionsGroupId,
      id: labelId
    },
    // TOP UA is assistive text and help link/text
    ...(hasTopUserAssistance && {
      topUserAssistanceProps: {
        id: topUaId as string,
        // Use reflow variant so that top UA doesn't have min height
        variant: 'reflow'
      }
    }),
    // Bottom UA is required indicator or messages
    ...(hasBottomUserAssistance && {
      bottomUserAssistanceProps: {
        id: bottomUaId as string,
        isRequiredShown: !!isRequired && !hasValue
      }
    })
  };
}
